import { useCallback, useEffect, useRef, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import { PhysicalCountService } from '../../../api/physicalCountCompat';
import type {
  PhysicalCountDetailItem,
  PhysicalCountSession,
  PhysicalCountSessionSummary,
} from '../../../types/physicalCount.types';
import { PHYSICAL_COUNT_ITEMS_PAGE_SIZE } from '../PhysicalCountDetailModal/physicalCountDetailGroups';

export interface UsePhysicalCountMissingItemsOptions {
  open: boolean;
  session: PhysicalCountSession | null;
}

export function usePhysicalCountMissingItems({
  open,
  session,
}: UsePhysicalCountMissingItemsOptions) {
  const [summary, setSummary] = useState<PhysicalCountSessionSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [missingItems, setMissingItems] = useState<PhysicalCountDetailItem[]>([]);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PHYSICAL_COUNT_ITEMS_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [initialLoading, setInitialLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const sessionIdRef = useRef<number | null>(null);

  const fetchSummary = useCallback(async (sessionId: number) => {
    try {
      const mapped = await PhysicalCountService.sessionsFindSummary(sessionId);
      if (sessionIdRef.current !== sessionId) {
        return null;
      }
      setSummary(mapped);
      setSummaryError(null);
      return mapped;
    } catch {
      if (sessionIdRef.current !== sessionId) {
        return null;
      }
      setSummaryError('No se pudo cargar el resumen de la sesión.');
      setSummary(null);
      return null;
    }
  }, []);

  const fetchMissingPage = useCallback(async (sessionId: number, page: number, limit: number) => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      const mapped = await PhysicalCountService.sessionsFindItems(sessionId, {
        group: 'missing',
        page,
        limit,
      });
      if (sessionIdRef.current !== sessionId) {
        return null;
      }

      setMissingItems(mapped.data);
      setTotalItems(mapped.meta.total);
      setCurrentPage(mapped.meta.page);
      return mapped;
    } catch {
      if (sessionIdRef.current !== sessionId) {
        return null;
      }
      setMissingItems([]);
      setTotalItems(0);
      setItemsError('No se pudieron cargar los faltantes.');
      return null;
    } finally {
      if (sessionIdRef.current === sessionId) {
        setItemsLoading(false);
      }
    }
  }, []);

  const refreshAfterWriteOff = useCallback(
    async (sessionId: number, preferredPage?: number) => {
      const newSummary = await fetchSummary(sessionId);
      if (newSummary == null) {
        return null;
      }

      const page = preferredPage ?? currentPage;
      const totalPages = Math.ceil(newSummary.missingCount / itemsPerPage) || 1;
      const targetPage = Math.min(page, totalPages);
      const mapped = await fetchMissingPage(sessionId, targetPage, itemsPerPage);
      if (mapped != null && mapped.data.length === 0 && targetPage > 1) {
        await fetchMissingPage(sessionId, targetPage - 1, itemsPerPage);
      }
      return newSummary;
    },
    [fetchSummary, fetchMissingPage, currentPage, itemsPerPage],
  );

  useEffect(() => {
    if (!open || session?.id == null) {
      setSummary(null);
      setSummaryError(null);
      setMissingItems([]);
      setItemsError(null);
      setTotalItems(0);
      setCurrentPage(1);
      setItemsPerPage(PHYSICAL_COUNT_ITEMS_PAGE_SIZE);
      sessionIdRef.current = null;
      return;
    }

    const sessionId = session.id;
    sessionIdRef.current = sessionId;
    setInitialLoading(true);
    setSummary(null);
    setSummaryError(null);
    setMissingItems([]);
    setItemsError(null);
    setTotalItems(0);
    setCurrentPage(1);
    setItemsPerPage(PHYSICAL_COUNT_ITEMS_PAGE_SIZE);

    Promise.all([
      fetchSummary(sessionId),
      fetchMissingPage(sessionId, 1, PHYSICAL_COUNT_ITEMS_PAGE_SIZE),
    ]).finally(() => {
      if (sessionIdRef.current === sessionId) {
        setInitialLoading(false);
      }
    });
  }, [open, session, fetchSummary, fetchMissingPage]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const sessionId = sessionIdRef.current;
      if (sessionId == null) {
        return;
      }
      setCurrentPage(newPage);
      void fetchMissingPage(sessionId, newPage, itemsPerPage);
    },
    [fetchMissingPage, itemsPerPage],
  );

  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const sessionId = sessionIdRef.current;
      if (sessionId == null) {
        return;
      }
      const newLimit = Number(event.target.value);
      setItemsPerPage(newLimit);
      setCurrentPage(1);
      void fetchMissingPage(sessionId, 1, newLimit);
    },
    [fetchMissingPage],
  );

  return {
    summary,
    summaryError,
    missingItems,
    itemsError,
    currentPage,
    itemsPerPage,
    totalItems,
    initialLoading,
    itemsLoading,
    refreshAfterWriteOff,
    handlePageChange,
    handleItemsPerPageChange,
  };
}
